import React, { useState, useEffect } from 'react';
import { Dropdown } from 'semantic-ui-react';
import { useNavigate } from 'react-router-dom';
import { courseAddFlrstInfo, studentData } from 'dsoapi';
import NextGatherInfoUrl from './NextGatherInfoUrl';

const idOptions = [
  {
    key: 'driverLicenseNumber',
    text: 'Driver License Number',
    value: 'driverLicenseNumber',
  },
  { key: 'ssnLast_5', text: 'SSN Last 5', value: 'ssnLast_5' },
  { key: 'alienNumber', text: 'Alien Number', value: 'alienNumber' },
  {
    key: 'admissionsNumber',
    text: 'Admissions Number',
    value: 'admissionsNumber',
  },
];

const GatherInfoFLRST = ({ uceId }) => {
  const [selectedIdType, setSelectedIdType] = useState(null);
  const [idValue, setIdValue] = useState('');
  const [gender, setGender] = useState(null);
  const [dob, setDob] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    const fetchStudentData = async () => {
      try {
        const resp = await studentData();
        console.log('Fetched studentData:', resp);

        const data = resp.data || {};
        console.log('Parsed data:', data);

        if (data.driverLicenseNumber) {
          setSelectedIdType('driverLicenseNumber');
          setIdValue(data.driverLicenseNumber);
        } else if (data.ssnLast_5) {
          setSelectedIdType('ssnLast_5');
          setIdValue(data.ssnLast_5);
        } else if (data.alienNumber) {
          setSelectedIdType('alienNumber');
          setIdValue(data.alienNumber);
        } else if (data.admissionsNumber) {
          setSelectedIdType('admissionsNumber');
          setIdValue(data.admissionsNumber);
        }

        if (data.gender) {
          setGender(data.gender);
        }

        if (data.dob && typeof data.dob === 'string') {
          const parsedDob = data.dob.split('T')[0]; // "2015-01-01"
          console.log('Setting DOB to:', parsedDob);
          setDob(parsedDob);
        } else {
          console.warn('DOB not set or invalid format');
        }
      } catch (e) {
        console.error('Error fetching student data:', e);
      }
    };

    fetchStudentData();
  }, [uceId]);

  function validate() {
    if (!selectedIdType || !idValue) {
      return {
        isValid: false,
        message: 'Please select and fill out one of the required ID fields.',
      };
    }
    if (selectedIdType === 'ssnLast_5') {
      if (!/^\d{5}$/.test(idValue)) {
        return {
          isValid: false,
          message: 'SSN Last 5 must be exactly 5 numeric digits',
        };
      }
    }
    if (selectedIdType === 'driverLicenseNumber') {
      const regex = /^[a-zA-Z][0-9]{12}$/;
      if (!regex.test(idValue)) {
        return {
          isValid: false,
          message:
            'Florida drivers license numbers consist of one letter and 12 numbers formatted as A123456789012',
        };
      }
    }
    if (!gender) {
      return {
        isValid: false,
        message: 'Gender is required',
      };
    }
    if (!dob) {
      return {
        isValid: false,
        message: 'Date of Birth is required',
      };
    }
    const dobDate = new Date(dob);
    const today = new Date();
    const ageInMilliseconds = today - dobDate;
    const ageInYears = ageInMilliseconds / (1000 * 60 * 60 * 24 * 365.25);

    if (ageInYears < 14.5 || ageInYears > 17) {
      return {
        isValid: false,
        message: 'Student must be between 14.5 and 17 years old.',
      };
    }

    return { isValid: true, message: '' };
  }

  const onSubmit = async (event) => {
    event.preventDefault();
    setShowValidation(true);
    setError('');
    const { isValid, message } = validate();
    if (!isValid) {
      setError(message);
      return;
    }
    setSubmitting(true);
    try {
      const request = {
        DriverLicenseNumber:
          selectedIdType === 'driverLicenseNumber' ? idValue : null,
        SsnLast_5: selectedIdType === 'ssnLast_5' ? idValue : null,
        AlienNumber: selectedIdType === 'alienNumber' ? idValue : null,
        AdmissionsNumber:
          selectedIdType === 'admissionsNumber' ? idValue : null,
        Gender: gender,
        Dob: dob,
      };
      await courseAddFlrstInfo(uceId, request);
      const url = await NextGatherInfoUrl(uceId);
      navigate(url);
    } catch (e) {
      setError('Failed to submit information. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Helper to get label and placeholder for the selected field
  const getFieldProps = () => {
    switch (selectedIdType) {
      case 'driverLicenseNumber':
        return {
          label: 'Driver License Number',
          placeholder: 'Driver License Number',
          maxLength: undefined,
          type: 'text',
        };
      case 'ssnLast_5':
        return {
          label: 'SSN Last 5',
          placeholder: 'SSN Last 5',
          maxLength: 5,
          type: 'text',
        };
      case 'alienNumber':
        return {
          label: 'Alien Number',
          placeholder: 'Alien Number',
          maxLength: undefined,
          type: 'text',
        };
      case 'admissionsNumber':
        return {
          label: 'Admissions Number',
          placeholder: 'Admissions Number',
          maxLength: undefined,
          type: 'text',
        };
      default:
        return {
          label: '',
          placeholder: '',
          maxLength: undefined,
          type: 'text',
        };
    }
  };

  const fieldProps = getFieldProps();

  return (
    <form className="ui form" onSubmit={onSubmit}>
      <div className="ui container">
        <h4 className="ui dividing header">Enter FLRST Course Information</h4>
        <div className="ui stackable grid">
          <div className="eight wide column">
            <div className="field">
              <label htmlFor="idType">Select 1 of the following:</label>
              <Dropdown
                name="idType"
                placeholder="Select one..."
                fluid
                selection
                options={idOptions}
                value={selectedIdType}
                onChange={(_, { value }) => {
                  setSelectedIdType(value);
                  setIdValue('');
                }}
              />
            </div>
            {selectedIdType && (
              <div className="field">
                <label htmlFor="idValue">{fieldProps.label}</label>
                <input
                  name="idValue"
                  placeholder={fieldProps.placeholder}
                  value={idValue}
                  onChange={(e) => setIdValue(e.target.value)}
                  maxLength={fieldProps.maxLength}
                  type={fieldProps.type}
                />
              </div>
            )}
            <div className="field">
              <label htmlFor="dob">Date of Birth</label>
              <input
                type="date"
                name="dob"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
              />
            </div>
          </div>
          <div className="eight wide column">
            <div className="field">
              <label htmlFor="gender">Gender</label>
              <Dropdown
                name="gender"
                style={{ maxHeight: '27rem' }}
                placeholder="Select gender"
                fluid
                selection
                options={[
                  { text: 'Male', value: 'M' },
                  { text: 'Female', value: 'F' },
                ]}
                value={gender}
                onChange={(_, { value }) => setGender(value)}
              />
            </div>
            <input
              className="ui primary button"
              type="submit"
              value={submitting ? 'Submitting...' : 'Submit'}
              disabled={submitting}
            />
            {showValidation && error && <p className="error">{error}</p>}
          </div>
        </div>
      </div>
    </form>
  );
};

export default GatherInfoFLRST;
